import React, { useEffect } from "react";

import { MdmObjectDto, SortDirection, useGetApartmentsPagedLazyQuery } from "gql/generated/types";
import { T } from "services/lang/LanguageService";
import { Link } from "react-router-dom";
import { TabPanel, TabView } from "primereact/tabview";
import { Path } from "react-hook-form";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster } from "../utils/utils";
import ObjectAndDeviceTableWrapper from "../components/ObjectAndDeviceTableWrapper";

const Nodes: React.FC = () => {
  const { projectId, userSettings } = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, "Child");

  const [tableOptions, setTableOptions] = useTableFilter("Child", {
    sortField: "identifier",
    reqSortField: "identifier",
    sortOrder: 1,
  });

  const [getNextObjects, { data, loading, previousData }] = useGetApartmentsPagedLazyQuery({
    onError: apolloErrorToaster,
  });
  const [getIsolatedData] = useGetApartmentsPagedLazyQuery();

  useEffect(() => {
    if (userSettings) {
      getNextObjects({
        variables: {
          input: {
            projectId,
            take: tableDetails?.rows || 10,
            skip: tableOptions.first,
            sort: [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: tableOptions.andFilters,
            filter: tableOptions.filter,
          },
        },
      });
    }
  }, [userSettings, projectId, tableOptions, tableDetails, getNextObjects]);

  const objectInfoBtn = (rowData: MdmObjectDto) => (
    <Link to={rowData.id!} title={T("mdm.apartment-info")}>
      {rowData.identifier}
    </Link>
  );

  const objectNameTemplate = (rowData: MdmObjectDto) => (
    <div className="truncate-text text-right lg:text-left" title={rowData.title ?? ""}>
      {rowData.title}
    </div>
  );

  const columns: TableColumn<MdmObjectDto>[] = [
    {
      field: "identifier",
      header: T("mdm.object-code"),
      body: objectInfoBtn,
      sortable: true,
      columnKey: "identifier",
    },
    {
      field: "title",
      header: T("word.object-name"),
      body: objectNameTemplate,
      sortable: true,
      columnKey: "objectName",
    },
    {
      field: "address.fullAddress",
      header: T("word.address"),
      sortable: true,
      columnKey: "address",
    },
    {
      field: "deviceRelations.length" as Path<MdmObjectDto>,
      header: T("mdm.device-count"),
      columnKey: "devices",
    },
  ];

  const objectData = data?.mdmApartmentsByProjectIdPaged.items || [];
  const previousObjectData = previousData?.mdmApartmentsByProjectIdPaged.items || [];
  const tableData = (data ? objectData : previousObjectData) as MdmObjectDto[];

  const total =
    data?.mdmApartmentsByProjectIdPaged.pageInfo?.total ??
    previousData?.mdmApartmentsByProjectIdPaged.pageInfo?.total;

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header={T("word.table")}>
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={tableData}
          tableName="Child"
          containerName="MdmObject"
          loading={loading}
          setTableOptions={setTableOptions}
          tableOptions={tableOptions}
          totalItems={total || 0}
          getQueryData={getNextObjects}
          getIsolatedData={getIsolatedData}
        />
      </TabPanel>
    </TabView>
  );
};

export default Nodes;
